import { createRef, useEffect, useState } from "react";
import {
  FlexFooterBlock,
  FlexFooterContainer,
  NavigationContainer,
  NavigationSection,
  ToggleButton,
  IconAccountCircle,
} from "@cdkglobal/radial";
import { WalkMeHelpButton } from "./WalkMeHelpButton";
import {
  StyledContextSwitcherContainer,
  StyledNavigationContextSwitcher,
} from "../../utils/styled";
import { getAppSwitcherMenuItems } from "./helpers/navigationMenuItems";
import UserMenu from "./UserMenu";
import { useUserContext } from "../../contexts/UserContext";
import { useIntl } from "react-intl";
import { SmartlingDropdown } from "../../SmartlingDropdown";
import i18n from "../../translate/i18next";
import SetupSelectorNavMenuItems from "./SetupSelectorNavMenu";
import { useRowDataContext } from "../../contexts/RowDataContext";
import { SCHEMA_TITLES } from "../LandingPage/constants";

export const Navigation = (): JSX.Element => {
  const { dispatch } = useRowDataContext();
  const { user, selected } = useUserContext();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const navigationContainerRef = createRef<HTMLDivElement>();
  const intl = useIntl();
  const [defaultValue, setDefaultValue] = useState(
    intl.formatMessage({ id: "cdcm.header.home", defaultMessage: "Setups" }),
  );

  useEffect(() => {
    setDefaultValue(
      intl.formatMessage({ id: "cdcm.header.home", defaultMessage: "Setups" }),
    );
  }, [i18n.language, intl]);

  useEffect(() => {
    if (selected.setup.value === SCHEMA_TITLES.CASH_CLEARING_SETUP) {
      handleCollapse();
    } else {
      handleExpand();
    }
  }, [selected.setup.value]);

  const handleCollapse = () => {
    setIsCollapsed(true);
  };

  const handleExpand = () => {
    setIsCollapsed(false);
  };

  return (
    <NavigationSection>
      <NavigationContainer
        id="navigation-container"
        isCollapsed={isCollapsed}
        isPositionStatic
        ref={navigationContainerRef}
      >
        <StyledContextSwitcherContainer>
          <StyledNavigationContextSwitcher
            key={i18n.language}
            closeContextSwitcherPopupOnOutsideClick
            dataTestId="global-navigation-context-switcher"
            defaultAvatarProps={{
              icon: <IconAccountCircle />,
              label: "App Switcher",
              variant: "icon",
            }}
            defaultSelectedValue={defaultValue}
            isOnGlobalHeader
            options={getAppSwitcherMenuItems(intl)}
            variant="appSwitcher"
            isCollapsed={isCollapsed}
          />
        </StyledContextSwitcherContainer>
        <StyledContextSwitcherContainer>
          <UserMenu isCollapsed={isCollapsed} />
        </StyledContextSwitcherContainer>
        <SetupSelectorNavMenuItems
          dispatch={dispatch}
          handleExpand={handleCollapse}
          isCollapsed={isCollapsed}
        />

        <FlexFooterContainer hasDivider isCollapsed={isCollapsed}>
          <FlexFooterBlock
            id="walkme-help-button"
            isCollapsed={isCollapsed}
            style={
              isCollapsed ? { marginBottom: "2.5rem", marginLeft: "-2rem" } : {}
            }
          >
            {user.user.loginId !== "" && (
              <WalkMeHelpButton isCollapsed={isCollapsed} />
            )}
          </FlexFooterBlock>

          <FlexFooterBlock
            id="smartling-dropdown-button"
            isCollapsed={isCollapsed}
            style={isCollapsed ? {} : { marginLeft: "3rem" }}
          >
            <SmartlingDropdown isCollapsed={isCollapsed} />
          </FlexFooterBlock>

          <FlexFooterBlock isCollapsed={isCollapsed}>
            <ToggleButton
              id="navigation-toggle-button"
              data-testid="navigation-toggle-button"
              collapsedToggleLabel={"Collapse navigation"}
              expandedToggleLabel={"Expand navigation"}
              iconClassName={"toggle-button-icon-classname"}
              isCollapsed={isCollapsed}
              onCollapse={handleCollapse}
              onExpand={handleExpand}
            />
          </FlexFooterBlock>
        </FlexFooterContainer>
      </NavigationContainer>
    </NavigationSection>
  );
};
